import { Checkbox, Stack, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import Flex from 'legacyfrontend/components/Flex';
import {
  cabinClassDic,
  useFlightsFilters,
} from 'legacyfrontend/contexts/FlightsFiltersContext';
import { scrollToTop } from 'legacyfrontend/helpers/scrollToTop';
import { clampPriceInput } from 'legacyfrontend/helpers/flightPriceFilters';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import useComputeOption from 'legacyfrontend/hooks/useComputeOption';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AccordionSection } from './components/AccordionSection';
import { BaggageFilter } from './components/BaggageFilter';
import { FlightsFilterByScale } from './components/FlightsFilterByScale';
import { PriceRangeFilter } from './components/PriceRangeFilter';
const filterKeysMap = {
  flights: 'Vuelos',
  airlines: 'Aerolíneas',
  cabinclass: 'Tipo de Cabina',
};

const FlightsAsideFilters = ({ isMobile }) => {
  const {
    filters,
    handleFilters,
    minMaxDataRangePrice,
    getAirlines,
    isLoading,
    filteredData,
    toggleBooleanFilter,
    handleResetFilters,
  } = useFlightsFilters();
  const computeOptions = useComputeOption();
  const { background } = useListingColors();

  const [filtros, setFiltros] = useState({
    airlines: ['Todas las aerolíneas'],
    cabinClass: ['economy', 'premiumEconomy', 'business', 'firstClass'], // Y, W, C, F
  });

  const [dropdownState, setDropdownState] = useState({
    flights: true,
    luggage: true,
    airlines: true,
    cabinClass: true,
  });

  const toggleDropdown = (filter) => {
    setDropdownState((prevState) => ({
      ...prevState,
      [filter]: !prevState[filter],
    }));
  };

  const handleDirectChange = useCallback(() => {
    handleFilters('scales', 'Todas las escalas');
    toggleBooleanFilter('direct');
  }, []);

  const handleScaleChange = useCallback(
    (scaleValue) => {
      const newScale = filters.scales === scaleValue ? 'Todas las escalas' : scaleValue;
      if (filters.direct) {
        toggleBooleanFilter('direct', false);
      }

      handleFilters('scales', newScale);
    },
    [filters.direct, filters.scales]
  );

  const handleFiltersCheckbox = useCallback(
    (filterKey, option) => {
      if (filterKey === 'airlines') {
        if (option === 'Todas las aerolíneas') {
          handleFilters(filterKey, ['Todas las aerolíneas']);
        } else {
          const currentAirlines = [...filters.airlines];
          const airlines = currentAirlines.filter((airline) => airline !== 'Todas las aerolíneas');

          if (currentAirlines.includes(option)) {
            const updatedAirlines = airlines.filter((airline) => airline !== option);
            handleFilters(
              filterKey,
              updatedAirlines.length ? updatedAirlines : ['Todas las aerolíneas']
            );
          } else {
            handleFilters(filterKey, [...airlines, option]);
          }
        }
      } else if (filterKey === 'cabinClass') {
        handleFilters(filterKey, cabinClassDic[option]);
      }
    },
    [filters.airlines]
  );

  const handleSliderChange = useCallback(
    (_, newValue) => {
      handleFilters('minPrice', newValue[0]);
      handleFilters('maxPrice', newValue[1] === newValue[0] ? newValue[1] + 1 : newValue[1]);
    },
    [handleFilters]
  );

  const handleInputChange = useCallback(
    ({ target: { value } }, filterKey) => {
      const price = value === '' ? 0 : Number(value);

      if (!Number.isFinite(price) || price < 0) return;

      const nextPrice = clampPriceInput(price, filterKey, {
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minMaxDataRangePrice,
      });

      handleFilters(filterKey, nextPrice);
    },
    [filters.maxPrice, filters.minPrice, handleFilters, minMaxDataRangePrice]
  );

  useEffect(() => {
    setFiltros((prev) => ({ ...prev, airlines: getAirlines }));
  }, [getAirlines]);

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <div
      className='min-h-screen bg-gray-100 flex relative'
      style={{
        borderRadius: '0.75rem',
        overflow: 'hidden',
      }}
    >
      <Stack
        bgcolor='#fff'
        height='max-content'
        maxWidth={!isMobile ? { xs: 250, lg: 300 } : {}}
        paddingBottom={14}
        paddingTop={4}
        paddingX={4}
        spacing={2}
        sx={{
          borderRadius: '0.75rem',
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {!isMobile && (
          <Flex className='aside-flights-filters__header justify-between'>
            <Typography variant='h5'>FILTROS</Typography>
            <button
              className={`aside-flights-filters__clean-btn ${
                isLoading ? 'cursor-not-allowed text-gray-300' : 'text-gray-700'
              }`}
              disabled={isLoading}
              onClick={handleResetFilters}
            >
              Limpiar
            </button>
          </Flex>
        )}

        <FlightsFilterByScale
          background={background}
          dropdownState={dropdownState}
          handleDirectChange={handleDirectChange}
          handleScaleChange={handleScaleChange}
          toggleDropdown={toggleDropdown}
        />
        <BaggageFilter
          background={background}
          dropdownState={dropdownState}
          toggleDropdown={toggleDropdown}
        />
        <PriceRangeFilter
          background={background}
          handleInputChange={handleInputChange}
          handleSliderChange={handleSliderChange}
          isLoading={isLoading}
          maxRangePrice={filters.maxPrice}
          minRangePrice={filters.minPrice}
          priceRange={minMaxDataRangePrice}
        />
        {Object.keys(filtros).map((filterKey, index) => (
          <Stack gap={3} key={`filter-dropdown-${filterKey}`}>
            <AccordionSection
              isOpen={dropdownState[filterKey]}
              onToggle={() => toggleDropdown(filterKey)}
              title={filterKeysMap[filterKey.toLocaleLowerCase()]}
            >
              {filtros[filterKey].map((option) => (
                <Stack alignItems='center' direction='row' flex={1} key={`filter-option-${option}`}>
                  <Checkbox
                    checked={
                      filterKey === 'cabinClass'
                        ? filters[filterKey].includes(cabinClassDic[option])
                        : filterKey === 'airlines'
                          ? filters[filterKey].includes(option)
                          : filters[filterKey].includes(option)
                    }
                    disabled={isLoading}
                    onClick={() => handleFiltersCheckbox(filterKey, option)}
                    sx={{
                      color: background,
                      '&.Mui-checked': {
                        color: background,
                      },
                      '&.Mui-disabled': {
                        color: '#ccc',
                      },
                    }}
                  />
                  <Typography>
                    {filterKey === 'cabinClass' ? computeOptions(option) : option}
                  </Typography>
                </Stack>
              ))}
            </AccordionSection>
            {index < Object.keys(filtros).length - 1 ? <Divider /> : null}
          </Stack>
        ))}
      </Stack>
    </div>
  );
};

export default FlightsAsideFilters;
